<template>
  <div class="w-full bg-white">
    <!-- Hero Section -->
    <div
      class="bg-gradient-to-br from-blue-50 via-gray-50 to-blue-100 px-3 py-16 lg:py-20"
    >
      <div
        class="mx-auto max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl flex flex-col lg:flex-row items-center gap-8 lg:gap-12"
      >
        <!-- Left Content -->
        <div class="flex-1 text-center lg:text-left">
          <JoszakiHeading
            class="text-4xl lg:text-5xl font-bold text-primary-dark"
          >
            {{ heroTitle }}
          </JoszakiHeading>

          <p class="text-base lg:text-lg text-gray-700 my-3">
            {{ $t("landing.priceOffer.hero.subtitle") }}
          </p>

          <div class="space-y-3 mb-6 inline-block text-left">
            <div class="flex items-start gap-2">
              <IconComponent
                icon="check"
                class="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
              />
              <p class="text-base lg:text-lg text-gray-700">
                {{ $t("landing.priceOffer.hero.subtitle1") }}
              </p>
            </div>
            <div class="flex items-start gap-2">
              <IconComponent
                icon="check"
                class="w-5 h-5 text-primary mt-0.5 flex-shrink-0"
              />
              <p class="text-base lg:text-lg text-gray-700">
                {{ $t("landing.priceOffer.hero.subtitle2") }}
              </p>
            </div>
          </div>

          <JoszakiButton
            v-if="$branding.hasFeature(FEATURES.REGISTRATION)"
            type="info"
            size="lg"
            :to="localePath({ name: 'demoPriceOffersIndexPage' })"
            @click="onTryItClick"
          >
            {{ showRegistrationForm ? $t("landing.priceOffer.hero.tryCta") : $t("landing.priceOffer.hero.cta") }}
          </JoszakiButton>
        </div>

        <!-- Right Side: Registration Form or Image Carousel -->
        <div
          class="flex-1 w-full"
          :class="showRegistrationForm ? 'max-w-xl lg:max-w-md' : 'max-w-xl'"
        >
          <JoszakiCard v-if="showRegistrationForm" class="flex flex-col gap-4">
            <JoszakiHeading :level="2" class="text-center">
              {{ $t("registration.meta.registration.name") }}
            </JoszakiHeading>
            <SimpleRegistrationForm class="w-full" />
          </JoszakiCard>
          <div v-else class="relative">
            <div class="relative w-full aspect-[4/3] overflow-hidden">
              <transition
                name="fade"
                mode="out-in"
                enter-active-class="transition-opacity duration-500 ease-in-out"
                leave-active-class="transition-opacity duration-500 ease-in-out"
                enter-from-class="opacity-0"
                leave-to-class="opacity-0"
              >
                <img
                  :key="currentImageIndex"
                  v-lazy-load :data-src="currentImage"
                  :alt="`${$t('landing.priceOffer.hero.title')} - ${
                    currentImageIndex + 1
                  }`"
                  class="absolute inset-0 w-full h-full object-contain rounded-lg shadow-xl"
                  data-not-lazy
                />
              </transition>
            </div>

            <!-- Pagination Dots -->
            <div class="flex justify-center gap-2 mt-4">
              <button
                v-for="(img, index) in images"
                :key="index"
                class="w-3 h-3 rounded-full transition-all"
                :class="
                  index === currentImageIndex
                    ? 'bg-primary w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                "
                @click="setImageIndex(index)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Boxes / Features Section -->
    <div v-if="serviceType" class="bg-white px-3 py-16">
      <div class="mx-auto max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl">
        <ServiceTypeInfoBoxes :service-type="serviceType" />
      </div>
    </div>

    <!-- Pricing Section -->
    <div
      v-if="serviceType"
      id="prices"
      class="bg-gray-50 px-3 py-16"
      :class="{ 'opacity-50': $fetchState.pending }"
    >
      <div class="mx-auto max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl">
        <div class="text-center mb-12">
          <JoszakiHeading :level="2" class="mb-3">
            {{ $t("landing.priceOffer.pricing.title") }}
          </JoszakiHeading>
          <p class="text-gray-500 text-lg">
            {{ $t("landing.priceOffer.pricing.subtitle") }}
          </p>
        </div>
        <ServiceTypePricingCards
          :service-type="serviceType"
          :show-free-register-button="showFreeRegisterButton"
          @freeClick="redirectToTryItPage"
          @freeRegisterClick="onFreeRegisterClick"
          @premiumClick="onPremiumButtonClick"
        />
      </div>
    </div>

    <!-- Stats Section -->
    <div class="bg-white px-3 py-16">
      <div class="mx-auto max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl">
        <div class="text-center mb-12">
          <JoszakiHeading :level="2" class="mb-3">
            {{ $t("landing.priceOffer.stats.title") }}
          </JoszakiHeading>
          <p class="text-gray-600 text-lg">
            {{ $t("landing.priceOffer.stats.subtitle") }}
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div class="text-center">
            <p class="text-5xl font-bold text-primary mb-2">10+</p>
            <p class="text-gray-600 font-bold text-lg">
              {{ $t("landing.priceOffer.stats.years") }}
            </p>
            <p class="text-gray-500">
              {{ $t("landing.priceOffer.stats.yearsDescription") }}
            </p>
          </div>
          <div class="text-center">
            <p class="text-5xl font-bold text-primary mb-2">50,000+</p>
            <p class="text-gray-600">
              {{ $t("landing.priceOffer.stats.professionals") }}
            </p>
            <p class="text-gray-500">
              {{ $t("landing.priceOffer.stats.professionalsDescription") }}
            </p>
          </div>
          <div class="text-center">
            <p class="text-5xl font-bold text-primary mb-2">1M+</p>
            <p class="text-gray-600">
              {{ $t("landing.priceOffer.stats.quotes") }}
            </p>
            <p class="text-gray-500">
              {{ $t("landing.priceOffer.stats.quotesDescription") }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="flex flex-row gap-4 items-start">
            <div
              class="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0"
            >
              <IconComponent icon="home" class="text-white !text-xl" />
            </div>
            <div>
              <h3 class="font-bold text-lg mb-1">
                {{ $t("landing.priceOffer.stats.feature1.title") }}
              </h3>
              <p class="text-gray-600">
                {{ $t("landing.priceOffer.stats.feature1.description") }}
              </p>
            </div>
          </div>
          <div class="flex flex-row gap-4 items-start">
            <div
              class="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0"
            >
              <IconComponent icon="users" class="text-white !text-xl" />
            </div>
            <div>
              <h3 class="font-bold text-lg mb-1">
                {{ $t("landing.priceOffer.stats.feature2.title") }}
              </h3>
              <p class="text-gray-600">
                {{ $t("landing.priceOffer.stats.feature2.description") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Testimonials Section -->
    <div class="bg-gray-50 px-3 py-16">
      <div class="mx-auto max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl">
        <div class="text-center mb-12">
          <JoszakiHeading :level="2" class="mb-3">
            {{ $t("landing.priceOffer.testimonials.title") }}
          </JoszakiHeading>
          <p class="text-gray-500 text-lg">
            {{ $t("landing.priceOffer.testimonials.subtitle") }}
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <JoszakiCard v-for="i in 3" :key="i" class="flex flex-col">
            <div class="flex flex-row gap-1 mb-3">
              <IconComponent
                v-for="star in 5"
                :key="star"
                icon="star"
                pack="fas"
                class="text-yellow-400 !text-sm"
              />
            </div>
            <blockquote class="text-gray-700 mb-4 flex-1">
              "{{ $t(`landing.priceOffer.testimonials.testimonial${i}.text`) }}"
            </blockquote>
            <div>
              <p class="font-bold">
                {{
                  $t(`landing.priceOffer.testimonials.testimonial${i}.author`)
                }}
              </p>
              <p class="text-sm text-gray-500">
                {{
                  $t(
                    `landing.priceOffer.testimonials.testimonial${i}.profession`
                  )
                }}
              </p>
            </div>
          </JoszakiCard>
        </div>
      </div>
    </div>

    <!-- Registration CTA Section -->
    <div
      v-if="showRegistrationForm"
      class="bg-gradient-to-br from-blue-50 via-gray-50 to-blue-100 px-3 py-16"
    >
      <div class="mx-auto max-w-xl">
        <JoszakiCard class="flex flex-col gap-4">
          <JoszakiHeading :level="2" class="text-center">
            {{ $t("registration.meta.registration.name") }}
          </JoszakiHeading>
          <SimpleRegistrationForm class="w-full" />
        </JoszakiCard>
      </div>
    </div>

    <!-- FAQ Section -->
    <div v-if="faqs.length" class="bg-white px-3 py-16">
      <div class="mx-auto max-w-3xl">
        <div class="text-center mb-12">
          <IconComponent
            icon="question-circle"
            pack="fas"
            class="text-primary !text-4xl mb-4 mx-auto"
          />
          <JoszakiHeading :level="2" class="mb-3">
            {{ $t("landing.priceOffer.faq.title") }}
          </JoszakiHeading>
          <p class="text-gray-500 text-lg">
            {{ $t("landing.priceOffer.faq.subtitle") }}
          </p>
        </div>
        <div
          v-for="bucket in faqBuckets"
          :key="bucket.id ?? 'ungrouped'"
          class="mb-8 last:mb-0"
        >
          <JoszakiHeading
            v-if="bucket.name && faqBuckets.length > 1"
            :level="3"
            class="mb-4"
          >
            {{ bucket.name }}
          </JoszakiHeading>
          <div class="flex flex-col gap-4">
            <JoszakiCollapsibleCard
              v-for="faq in bucket.faqs"
              :key="faq.id"
              :title="faq.question"
            >
              <p class="text-gray-600 pt-2">
                {{ faq.answer }}
              </p>
            </JoszakiCollapsibleCard>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  useContext,
  useFetch,
  ref,
  computed,
  useRouter,
  onMounted,
  onBeforeUnmount,
  useRoute,
} from "@nuxtjs/composition-api";
import useFeatures from "~/composables/useFeatures";
import useFaqGroupedBuckets from "~/composables/useFaqGroupedBuckets";
import GET_SERVICE_TYPE_BY_TYPE_QUERY from "@/components/_refactored/indexPage/getServiceTypeByType.graphql";
import FAQS_QUERY from "@/components/_refactored/indexPage/getFaqs.graphql";
import GET_PROFESSION_BY_SEO_NAME from "~/graphql/profession.graphql";
import ServiceTypePricingCards from "@/components/_refactored/subscription/ServiceTypePricingCards.vue";
import ServiceTypeInfoBoxes from "@/components/_refactored/subscription/ServiceTypeInfoBoxes.vue";
import SimpleRegistrationForm from "~/components/_refactored/registration/SimpleRegistrationForm.vue";
import { useUserStore } from "~/stores/user";

export default {
  name: "PriceOfferIndexPage",
  components: {
    ServiceTypePricingCards,
    ServiceTypeInfoBoxes,
    SimpleRegistrationForm,
  },
  props: {
    showRegistrationForm: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { FEATURES, hasFeature } = useFeatures();
    const { $query, i18n, localePath, $trackers, $logger } = useContext();
    const router = useRouter();
    const route = useRoute();
    const userStore = useUserStore();

    const showFreeRegisterButton = computed(
      () => !userStore.me && hasFeature(FEATURES.REGISTRATION)
    );

    const serviceType = ref(null);
    const currentImageIndex = ref(0);
    const carouselInterval = ref(null);
    const faqs = ref([]);
    const faqBuckets = useFaqGroupedBuckets(faqs, {
      otherBucketName: computed(() => i18n.t("faqPage.otherGroup")),
    });
    const profession = ref(null);

    $logger.info(
      `PriceOfferPage setup query, route: ${
        route.value.fullPath
      }, ${route.value.query.toString()}`
    );

    // Image carousel setup - use require.context to load all available images
    const imageContext = require.context(
      "~/assets/price-offer",
      false,
      /landing-\d+-[\w-]+\.png$/
    );
    const availableImages = imageContext.keys();

    const getLocalizedImage = (imageNumber, locale) => {
      const localizedPath = `./landing-${imageNumber}-${locale}.png`;
      const fallbackPath = `./landing-${imageNumber}-en-US.png`;

      if (availableImages.includes(localizedPath)) {
        return imageContext(localizedPath);
      }
      return imageContext(fallbackPath);
    };

    const images = computed(() => {
      const locale = i18n.locale.replace("_", "-");
      return [
        getLocalizedImage(1, locale),
        getLocalizedImage(2, locale),
      ];
    });

    const startCarousel = () => {
      carouselInterval.value = setInterval(() => {
        currentImageIndex.value =
          (currentImageIndex.value + 1) % images.value.length;
      }, 4000); // Change image every 4 seconds
    };

    const currentImage = computed(
      () => images.value?.[currentImageIndex.value]
    );

    // Computed property for hero title with profession name
    const heroTitle = computed(() => {
      if (profession.value?.name) {
        return i18n.t("landing.priceOffer.hero.titleWithProfession", {
          profession: profession.value.name.toLowerCase(),
        });
      }
      return i18n.t("landing.priceOffer.hero.title");
    });

    const setImageIndex = (index) => {
      if (carouselInterval.value) {
        clearInterval(carouselInterval.value);
        carouselInterval.value = null;
      }
      currentImageIndex.value = index;
      startCarousel();
    };

    const redirectToTryItPage = () => {
      $trackers.trackClick("PriceOfferLandingFreePackageTryIt");
      router.push(localePath({ name: "demoPriceOffersCreatePage" }));
    };

    const onTryItClick = () => {
      $trackers.trackClick("PriceOfferLandingHeroCta");
    };

    const onFreeRegisterClick = () => {
      $trackers.trackClick("PriceOfferLandingFreePackageRegister");
      router.push(localePath({ name: "priceOfferRegistrationPage" }));
    };

    const onPremiumButtonClick = () => {
      if (userStore.me) {
        router.push(
          localePath({
            name: "subscriptionServiceTypePage",
            params: { serviceTypeSeoName: serviceType.value.seoName },
          })
        );
      } else {
        $trackers.trackClick("PriceOfferLandingPaidPackageRegister");
        router.push(
          localePath({
            name: "priceOfferRegistrationPage",
          })
        );
      }
    };

    useFetch(async () => {
      try {
        // Fetch service type by type
        const { serviceTypes } = await $query(GET_SERVICE_TYPE_BY_TYPE_QUERY, {
          type: "price_offer",
        });

        serviceType.value = serviceTypes?.[0] || null;

        // Fetch FAQs for price offer creator
        const faqsResult = await $query(FAQS_QUERY, {
          type: "price_offer_professional",
        });

        faqs.value = faqsResult.faqs || [];

        // Fetch profession by seo name from query parameter
        const professionParam = i18n.t(
          "landing.priceOffer.professionQueryParam"
        );
        const professionSeoName = route.value.query[professionParam];

        if (professionSeoName) {
          const professionResult = await $query(
            GET_PROFESSION_BY_SEO_NAME,
            {
              professionSeoName,
            },
            {
              showErrorPage: false,
              showErrorToast: false,
            }
          );

          profession.value = professionResult?.profession || null;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    });

    // Lifecycle hooks in setup
    onMounted(() => {
      startCarousel();
      $trackers.pageview({
        pagename: $trackers.pageNames.PRICE_OFFER_LANDING_PAGE,
      });
    });

    onBeforeUnmount(() => {
      if (carouselInterval.value) {
        clearInterval(carouselInterval.value);
        carouselInterval.value = null;
      }
      $trackers.pageleave({
        pagename: $trackers.pageNames.PRICE_OFFER_LANDING_PAGE,
      });
    });

    return {
      FEATURES,
      showFreeRegisterButton,
      serviceType,
      images,
      currentImage,
      currentImageIndex,
      setImageIndex,
      redirectToTryItPage,
      onTryItClick,
      onFreeRegisterClick,
      onPremiumButtonClick,
      faqs,
      faqBuckets,
      profession,
      heroTitle,
    };
  },
  head() {
    const description = this.$t("landing.priceOffer.hero.subtitle1");
    return {
      title: this.$t("landing.meta.metaTitle"),
      meta: [
        {
          hid: "og:title",
          property: "og:title",
          content: this.$t("landing.meta.metaTitle"),
        },
        {
          hid: "description",
          name: "description",
          content: description,
        },
        {
          hid: "og:description",
          property: "og:description",
          content: description,
        },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}`,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: this.$helpers.topDomain,
        },
      ],
    };
  },
};
</script>
